import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import Card from "shared/Card";
import { largeImageWarningForSrc } from "../imageSizeWarning";
import CollapseToggle from "../CollapseToggle";
import styles from "./ProductImageCard.module.css";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export function ProductImageCard({
  images = [],
  onSelectFile = () => {},
  onChange = null,
}) {
  const fileInputRef = React.useRef(null);
  const [dragIdx, setDragIdx] = React.useState(null);
  const [overIdx, setOverIdx] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(false);

  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    onSelectFile(files);
    event.target.value = "";
  };

  const move = (from, to) => {
    if (!onChange) return;
    if (to < 0 || to >= images.length || from === to) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const remove = (idx) => {
    if (!onChange) return;
    onChange(images.filter((_, i) => i !== idx));
  };

  const handleDrop = (to) => {
    if (dragIdx !== null) move(dragIdx, to);
    setDragIdx(null);
    setOverIdx(null);
  };

  const canReorder = typeof onChange === "function";

  return (
    <Card className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>Product Images</span>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.selectBtn}
            onClick={handleSelectClick}
            title="Add one or more images to this product from your computer."
          >
            + Add
          </button>
          <CollapseToggle
            collapsed={collapsed}
            onToggle={() => setCollapsed((v) => !v)}
          />
        </div>
      </header>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {!collapsed && (
        <>
          <div className={styles.divider} />

          <div className={styles.body}>
            {images.length > 0 ? (
              <ul className={styles.imageList}>
                {images.map((img, idx) => {
                  const src = typeof img === "string" ? img : img?.url || "";
                  const key =
                    typeof img === "string"
                      ? `${img}-${idx}`
                      : img?.id || `${idx}`;
                  return (
                    <li
                      key={key}
                      className={classNames(
                        styles.imageRow,
                        canReorder && styles.imageRowDraggable,
                        dragIdx === idx && styles.imageRowDragging,
                        overIdx === idx &&
                          dragIdx !== null &&
                          styles.imageRowOver
                      )}
                      draggable={canReorder}
                      onDragStart={(e) => {
                        if (!canReorder) return;
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", String(idx));
                        setDragIdx(idx);
                      }}
                      onDragOver={(e) => {
                        if (!canReorder) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        setOverIdx(idx);
                      }}
                      onDrop={() => canReorder && handleDrop(idx)}
                      onDragEnd={() => {
                        setDragIdx(null);
                        setOverIdx(null);
                      }}
                    >
                      {canReorder && (
                        <span className={styles.moveBtns}>
                          <button
                            type="button"
                            className={styles.moveBtn}
                            onClick={() => move(idx, idx - 1)}
                            disabled={idx === 0}
                            aria-label="Move image up"
                            title="Move this image earlier in the product's image order."
                          >
                            <FontAwesomeIcon icon={faChevronUp} />
                          </button>
                          <button
                            type="button"
                            className={styles.moveBtn}
                            onClick={() => move(idx, idx + 1)}
                            disabled={idx === images.length - 1}
                            aria-label="Move image down"
                            title="Move this image later in the product's image order."
                          >
                            <FontAwesomeIcon icon={faChevronDown} />
                          </button>
                        </span>
                      )}
                      <div className={styles.thumb}>
                        <img src={src} alt={`Product ${idx + 1}`} />
                      </div>
                      <span className={styles.imageLabel}>
                        {`${ordinal(idx + 1)} Image`}
                        {largeImageWarningForSrc(src) && (
                          <span className={styles.sizeWarning}>
                            {largeImageWarningForSrc(src)}
                          </span>
                        )}
                      </span>
                      {canReorder && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => remove(idx)}
                          aria-label="Remove image"
                          title="Remove this image from the product. The image stays in the media library."
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className={styles.placeholder}>No images yet.</p>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

ProductImageCard.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  onSelectFile: PropTypes.func,
  onChange: PropTypes.func,
};

export default ProductImageCard;
